import * as yup from 'yup';
import { PracticeFocus } from './constants/PracticeFocus.enum';
import { getZipCodeValidationSchema } from './locationsHelper';
export interface PracticeLocationValidationOptions {
    includeFteDvm?: boolean;
    includePracticeFocus?: boolean;
    includePhone?: boolean;
    includeWebsite?: boolean;
}

export const getPracticeLocationItemSchema = (
    axios: any,
    zipCodeCache: Record<string, boolean>,
    options: PracticeLocationValidationOptions = {}
) => {
    const {
        includeFteDvm = true,
        includePracticeFocus = true,
        includePhone = true,
        includeWebsite = true,
    } = options;

    const baseSchema: any = {
        practiceLocationName: yup
            .string()
            .trim()
            .required('Location name is required'),
        address: yup.string().trim().required('Street address is required'),
        city: yup.string().trim().required('City is required'),
        state: yup.string().trim().required('State is required'),
        zipCode: getZipCodeValidationSchema(axios, zipCodeCache),
    };

    if (includePhone) {
        baseSchema.phone = yup
            .string()
            .trim()
            .length(10, 'Phone number must be 10 digits')
            .required('Phone number is required');
    }

    if (includeWebsite) {
        baseSchema.website = yup
            .string()
            .trim()
            .required('Website is required');
    }

    if (includeFteDvm) {
        baseSchema.fteDvm = yup
            .number()
            .test('fteDvm-required', 'FTE DVM is required', function (value) {
                return value !== 0 && value !== null && value !== undefined;
            })
            .required('FTE DVM is required');
    }

    if (includePracticeFocus) {
        baseSchema.practiceFocus = yup
            .number()
            .test(
                'practiceFocus-required',
                'Service focus is required',
                function (value) {
                    return value !== 0 && value !== null && value !== undefined;
                }
            )
            .required('Service focus is required');

        baseSchema.otherPracticeType = yup.string().when('practiceFocus', {
            is: PracticeFocus.Other,
            then: (schema) =>
                schema.required('Please specify the service focus type'),
            otherwise: (schema) => schema.notRequired(),
        });
    }

    return yup.object().shape(baseSchema);
};

export const getPracticeLocationDataArraySchema = (
    axios: any,
    zipCodeCache: Record<string, boolean>,
    options: PracticeLocationValidationOptions = {}
) => {
    return yup
        .array()
        .of(getPracticeLocationItemSchema(axios, zipCodeCache, options));
};

export const getPracticeLocationDataWithCountValidation = (
    axios: any,
    zipCodeCache: Record<string, boolean>,
    options: PracticeLocationValidationOptions = {}
) => {
    return yup
        .array()
        .test(
            'locations-length',
            'Number of locations must match the specified count',
            function (value) {
                const numberOfLocations = this.parent.numberOfLocations;
                if (!numberOfLocations || numberOfLocations <= 0) {
                    return this.createError({
                        message: 'Please specify number of locations',
                    });
                }
                if (!value || value.length !== numberOfLocations) {
                    return this.createError({
                        message: `Please provide data for all ${numberOfLocations} location(s)`,
                    });
                }
                return true;
            }
        )
        .of(getPracticeLocationItemSchema(axios, zipCodeCache, options));
};
